import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import I18nText from "../components/I18nText";
import ReinforcementModal from "../components/ReinforcementModal";
import ValuePanel from "../components/ValuePanel";
import { track } from "../lib/analytics";
import {
    submitQuoteRequest,
    sharePeer,
    formatApiError,
    badRecipientIndices,
    setProposalPassword,
    validatePassword,
    requestEmployerSetupEmail,
} from "../lib/api";
import { formatPhone, isValidEmail, titleCaseName } from "../lib/dataHygiene";
import { MIN_EMPLOYEE_COUNT, SHARE_MAX_PEER_RECIPIENTS } from "../config/constants";

import "./SetPassword.css";

const PAYROLL_OPTS = ["weekly", "biweekly", "semimonthly", "monthly"];
// Minimum computing-animation time — psychological cue that "something happened"
// without feeling fake. Ported from `quote.html#minComputeMs`. Strict-spec
// floor is 3000ms; we add a 200ms safety buffer so layout paint / state
// commit never causes the test harness to see sub-3s visibility.
const MIN_COMPUTE_MS = 3200;

/* -------------------------------------------------------------------- */
/* 8-field quote form (Phase 1.5a)                                       */
/* -------------------------------------------------------------------- */
function QuoteForm({ onAccepted }) {
    const { t } = useTranslation();
    const [values, setValues] = useState({
        companyName: "",
        zipCode: "",
        employeeCount: "",
        payrollFrequency: "biweekly",
        name: "",
        title: "",
        email: "",
        cellPhone: "",
    });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [apiError, setApiError] = useState("");

    useEffect(() => {
        track("quote_form_view", {});
    }, []);

    const set = (k) => (e) =>
        setValues((v) => ({ ...v, [k]: e.target.value }));

    const blurName = (k) => (e) =>
        setValues((v) => ({ ...v, [k]: titleCaseName(e.target.value) }));
    const blurEmail = (k) => (e) =>
        setValues((v) => ({ ...v, [k]: e.target.value.trim() }));
    const blurPhone = (k) => (e) => {
        const f = formatPhone(e.target.value);
        setValues((v) => ({ ...v, [k]: f.valid ? f.display : e.target.value.trim() }));
    };

    const validate = () => {
        const next = {};
        if (!values.companyName.trim()) next.companyName = t("errors.required");
        if (!values.zipCode.trim()) next.zipCode = t("errors.required");
        else if (!/^\d{5}$/.test(values.zipCode.trim()))
            next.zipCode = t("employers.quote.errorZip");
        const n = parseInt(values.employeeCount, 10);
        if (!values.employeeCount) next.employeeCount = t("errors.required");
        else if (Number.isNaN(n) || n < 1) next.employeeCount = t("errors.number");
        else if (n < MIN_EMPLOYEE_COUNT)
            next.employeeCount = t("errors.minEmployees", { min: MIN_EMPLOYEE_COUNT });
        if (!values.name.trim()) next.name = t("errors.required");
        if (!values.title.trim()) next.title = t("errors.required");
        if (!values.email.trim()) next.email = t("errors.required");
        else if (!isValidEmail(values.email.trim()))
            next.email = t("auth.common.emailInvalid");
        if (!values.cellPhone.trim()) next.cellPhone = t("errors.required");
        else if (!formatPhone(values.cellPhone).valid)
            next.cellPhone = t("auth.common.phoneInvalid");
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setApiError("");
        track("quote_request_submit", {
            employeeCount: parseInt(values.employeeCount, 10) || null,
            payrollFrequency: values.payrollFrequency,
        });
        if (!validate()) return;
        setSubmitting(true);
        try {
            const res = await submitQuoteRequest({
                companyName: values.companyName.trim(),
                zipCode: values.zipCode.trim(),
                employeeCount: parseInt(values.employeeCount, 10),
                payrollFrequency: values.payrollFrequency,
                name: values.name.trim(),
                title: values.title.trim(),
                email: values.email.trim(),
                cellPhone: formatPhone(values.cellPhone).digits || values.cellPhone.trim(),
            });
            track("quote_request_success", {
                proposalId: res.proposalId || res.requestId,
            });
            onAccepted({
                proposalId: res.proposalId,
                requestId: res.requestId,
                redirectUrl: res.redirectUrl,
                firstName: values.name.trim().split(" ")[0],
                submitterEmail: values.email.trim(),
                companyName: values.companyName.trim(),
                employeeCount: parseInt(values.employeeCount, 10),
                payrollFrequency: values.payrollFrequency,
                preliminaryQuote: res.preliminaryQuote,
                frequencyLabel: res.frequencyLabel,
                frequencyShort: res.frequencyShort,
                stateCode: res.stateCode,
                stateName: res.stateName,
            });
        } catch (err) {
            const msg = formatApiError(err) || t("errors.generic");
            setApiError(msg);
            track("quote_request_error", { message: msg });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form className="quote-form" onSubmit={onSubmit} data-testid="quote-form">
            <div className="section-label mono">{t("employers.quote.sectionLabel")}</div>
            <h3>
                <I18nText i18nKey="employers.quote.h3" />
            </h3>
            <p className="subtitle">{t("employers.quote.subtitle")}</p>

            {/* Section: Your company */}
            <div className="form-section-head mono">
                {t("employers.quote.sectionCompany")}
            </div>
            <div className="form-grid">
                <div className="form-field">
                    <label className="mono">{t("employers.quote.labelCompany")}</label>
                    <input
                        type="text"
                        placeholder={t("employers.quote.placeholderCompany")}
                        value={values.companyName}
                        onChange={set("companyName")}
                        data-testid="quote-company"
                    />
                    {errors.companyName && (
                        <span className="field-error">{errors.companyName}</span>
                    )}
                </div>
                <div className="form-field">
                    <label className="mono">{t("employers.quote.labelZip")}</label>
                    <input
                        type="text"
                        inputMode="numeric"
                        pattern="\d{5}"
                        maxLength={5}
                        placeholder={t("employers.quote.placeholderZip")}
                        value={values.zipCode}
                        onChange={(e) => {
                            // Preserve leading zeros — strip non-digits,
                            // cap at 5 chars. Using a plain string (not
                            // Number) keeps "02101" intact.
                            const digits = e.target.value.replace(/\D/g, "").slice(0, 5);
                            setValues((v) => ({ ...v, zipCode: digits }));
                        }}
                        data-testid="quote-zip"
                        autoComplete="postal-code"
                    />
                    <span className="field-hint">
                        {t("employers.quote.hintZip")}
                    </span>
                    {errors.zipCode && (
                        <span className="field-error" data-testid="quote-zip-error">
                            {errors.zipCode}
                        </span>
                    )}
                </div>
                <div className="form-field">
                    <label className="mono">{t("employers.quote.labelEmployees")}</label>
                    <input
                        type="number"
                        placeholder={t("employers.quote.placeholderEmployees")}
                        min="1"
                        value={values.employeeCount}
                        onChange={set("employeeCount")}
                        data-testid="quote-employees"
                    />
                    {errors.employeeCount && (
                        <span className="field-error">{errors.employeeCount}</span>
                    )}
                </div>
                <div className="form-field">
                    <label className="mono">{t("employers.quote.labelPayroll")}</label>
                    <select
                        value={values.payrollFrequency}
                        onChange={set("payrollFrequency")}
                        data-testid="quote-payroll"
                    >
                        {PAYROLL_OPTS.map((o) => (
                            <option key={o} value={o}>
                                {t(`employers.quote.payroll.${o}`)}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Section: You */}
            <div className="form-section-head mono" style={{ marginTop: "1.5rem" }}>
                {t("employers.quote.sectionYou")}
            </div>
            <div className="form-grid">
                <div className="form-field">
                    <label className="mono">{t("employers.quote.labelName")}</label>
                    <input
                        type="text"
                        placeholder={t("employers.quote.placeholderName")}
                        value={values.name}
                        onChange={set("name")}
                        onBlur={blurName("name")}
                        data-testid="quote-name"
                    />
                    {errors.name && <span className="field-error">{errors.name}</span>}
                </div>
                <div className="form-field">
                    <label className="mono">{t("employers.quote.labelTitle")}</label>
                    <input
                        type="text"
                        placeholder={t("employers.quote.placeholderTitle")}
                        value={values.title}
                        onChange={set("title")}
                        data-testid="quote-title"
                    />
                    {errors.title && <span className="field-error">{errors.title}</span>}
                </div>
                <div className="form-field">
                    <label className="mono">{t("employers.quote.labelEmail")}</label>
                    <input
                        type="email"
                        placeholder={t("employers.quote.placeholderEmail")}
                        value={values.email}
                        onChange={set("email")}
                        onBlur={blurEmail("email")}
                        data-testid="quote-email"
                    />
                    <span className="field-hint">
                        {t("employers.quote.hintEmail")}
                    </span>
                    {errors.email && <span className="field-error">{errors.email}</span>}
                </div>
                <div className="form-field">
                    <label className="mono">{t("employers.quote.labelCellPhone")}</label>
                    <input
                        type="tel"
                        placeholder={t("employers.quote.placeholderCellPhone")}
                        value={values.cellPhone}
                        onChange={set("cellPhone")}
                        onBlur={blurPhone("cellPhone")}
                        data-testid="quote-cell-phone"
                    />
                    {errors.cellPhone && (
                        <span className="field-error">{errors.cellPhone}</span>
                    )}
                </div>
            </div>

            {apiError && (
                <div
                    className="field-error"
                    data-testid="quote-api-error"
                    style={{ marginBottom: "1rem" }}
                >
                    {apiError}
                </div>
            )}
            <button
                type="submit"
                className="btn-submit"
                disabled={submitting}
                data-testid="quote-submit"
            >
                {submitting ? t("employers.quote.submitting") : t("employers.quote.submit")}
            </button>
            <p className="quote-instant-note mono">
                {t("employers.quote.instantNote")}
            </p>
        </form>
    );
}

/* -------------------------------------------------------------------- */
/* Computing-state animation                                             */
/* -------------------------------------------------------------------- */
function ComputingState({ onDone }) {
    const { t } = useTranslation();
    const [activeStep, setActiveStep] = useState(0);
    const [done, setDone] = useState(false);
    const containerRef = useRef(null);
    const steps = t("employers.computing.steps", { returnObjects: true }) || [];

    useEffect(() => {
        track("quote_computing_view", {});
        // Phase 3e mobile polish — anchor the computing graphic to the
        // top of the viewport so the user doesn't land mid-scroll. Uses
        // `scrollIntoView({block:'start'})` which Safari respects.
        if (containerRef.current) {
            try {
                containerRef.current.scrollIntoView({
                    behavior: "smooth", block: "start",
                });
            } catch (_e) { /* best-effort */ }
        }
        const timers = [];
        steps.forEach((_, i) => {
            timers.push(setTimeout(() => setActiveStep(i + 1), (i + 1) * 800));
        });
        timers.push(
            setTimeout(() => {
                setDone(true);
                onDone();
            }, MIN_COMPUTE_MS),
        );
        return () => timers.forEach(clearTimeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            ref={containerRef}
            className={`quote-computing ${done ? "is-done" : ""}`}
            data-testid="quote-computing"
            role="status"
            aria-live="polite"
        >
            <div className="section-label mono">
                {t("employers.computing.sectionLabel")}
            </div>
            <h3>{t("employers.computing.h3")}</h3>
            <ul className="computing-steps">
                {steps.map((label, i) => {
                    const state =
                        i < activeStep ? "is-done" : i === activeStep ? "is-active" : "";
                    return (
                        <li
                            key={i}
                            className={`computing-step ${state}`}
                            data-testid={`computing-step-${i}`}
                        >
                            <span className="computing-dot" aria-hidden="true" />
                            <span>{label}</span>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

/* -------------------------------------------------------------------- */
/* Instant result screen                                                 */
/* -------------------------------------------------------------------- */
function ResultScreen({ accepted, onViewProposal, onShowReinforcement }) {
    const { t } = useTranslation();
    const resultRef = useRef(null);
    const pq = accepted.preliminaryQuote || {};

    useEffect(() => {
        track("quote_result_view", {
            proposalId: accepted.proposalId,
            state: accepted.stateCode,
        });
        if (resultRef.current) {
            resultRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [accepted.proposalId, accepted.stateCode]);

    const freqShort = accepted.frequencyShort || "paycheck";
    const lower = pq.lower_per_period;
    const upper = pq.upper_per_period;
    const rangeText =
        lower != null && upper != null
            ? `$${Math.round(lower)} – $${Math.round(upper)}`
            : "—";
    const annualNum = pq.projected_annual_company_premium || 0;
    const annualText = `$${Math.round(annualNum).toLocaleString()}`;
    const stateLabel = accepted.stateName || t("employers.result.stateFallback");

    return (
        <div className="quote-result" ref={resultRef} data-testid="quote-result">
            <div className="section-label mono">
                {t("employers.result.sectionLabel")}
            </div>
            <h2>
                <I18nText i18nKey="employers.result.h2" />
            </h2>
            <div className="result-range" data-testid="result-range">
                <span className="result-range-value">{rangeText}</span>
                <div className="result-range-frame mono">
                    {t("employers.result.frame", { frequencyShort: freqShort })}
                </div>
            </div>
            <p className="result-context">
                {t("employers.result.context", { state: stateLabel })}
            </p>

            <div className="result-stats" data-testid="result-stats">
                <div className="result-stat">
                    <div className="mono stat-label">
                        {t("employers.result.stats.eligible")}
                    </div>
                    <div className="stat-value">{accepted.employeeCount}</div>
                </div>
                <div className="result-stat">
                    <div className="mono stat-label">
                        {t("employers.result.stats.companyImpact")}
                    </div>
                    <div className="stat-value">$0</div>
                    <div className="stat-sub mono">
                        {t("employers.result.stats.companyImpactSub")}
                    </div>
                </div>
                <div className="result-stat">
                    <div className="mono stat-label">
                        {t("employers.result.stats.projectedAnnual")}
                    </div>
                    <div className="stat-value">{annualText}</div>
                    <div className="stat-sub mono">
                        {t("employers.result.stats.projectedAnnualSub")}
                    </div>
                </div>
                <div className="result-stat">
                    <div className="mono stat-label">
                        {t("employers.result.stats.situs")}
                    </div>
                    <div className="stat-value">{stateLabel}</div>
                </div>
            </div>

            <div className="result-disclosure mono">
                {t("employers.result.disclosure")}
            </div>

            <div className="result-cta-row">
                <button
                    type="button"
                    className="btn-submit btn-primary"
                    onClick={onViewProposal}
                    data-testid="result-view-proposal"
                >
                    {t("employers.result.ctaProposal")}
                </button>
                <button
                    type="button"
                    className="btn-ghost"
                    onClick={onShowReinforcement}
                    data-testid="result-reinforce-cta"
                >
                    {t("employers.result.ctaReinforce")}
                </button>
                <a
                    href="#peer-share"
                    className="btn-ghost"
                    data-testid="result-share-link"
                    onClick={(e) => {
                        e.preventDefault();
                        document
                            .querySelector("[data-testid='post-quote']")
                            ?.scrollIntoView({ behavior: "smooth" });
                    }}
                >
                    {t("employers.result.ctaShare")}
                </a>
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------- */
/* PostQuoteSetPasswordModal — A3 blocking modal. Appears immediately   */
/* after quote generation and nudges the user to secure their account.  */
/* POSTs to /api/proposal/password/set, which upserts an employer_users */
/* row keyed by email + proposal_id. If submit fails, we silently fire  */
/* the password-setup email so the user can recover from their inbox.   */
/* -------------------------------------------------------------------- */
const SP_DEBOUNCE_MS = 300;
const SP_MIN_LEN = 8;

function PostQuoteSetPasswordModal({ open, proposalId, firstName, email, onDone }) {
    const { t } = useTranslation();
    const [pw1, setPw1] = useState("");
    const [pw2, setPw2] = useState("");
    const [validation, setValidation] = useState({
        valid: false, breached: false, length_ok: false, checking: false,
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitErr, setSubmitErr] = useState("");
    const [skipping, setSkipping] = useState(false);
    const [skipSent, setSkipSent] = useState(false);
    const debounceRef = useRef(null);
    const firstInputRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        track("password_modal_view", { proposal_id: proposalId });
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        setTimeout(() => firstInputRef.current?.focus(), 30);
        return () => { document.body.style.overflow = prevOverflow; };
    }, [open, proposalId]);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (pw1.length < SP_MIN_LEN) {
            setValidation({
                valid: false, breached: false, length_ok: false, checking: false,
            });
            return;
        }
        setValidation((v) => ({ ...v, length_ok: true, checking: true }));
        debounceRef.current = setTimeout(async () => {
            try {
                const res = await validatePassword(pw1);
                setValidation({ ...res, checking: false });
            } catch (_) {
                setValidation({
                    valid: true, breached: false, length_ok: true, checking: false,
                });
            }
        }, SP_DEBOUNCE_MS);
        return () => debounceRef.current && clearTimeout(debounceRef.current);
    }, [pw1]);

    if (!open) return null;

    const matchOk = pw2.length > 0 && pw1 === pw2;
    const canSubmit = !submitting
        && validation.length_ok
        && !validation.breached
        && !validation.checking
        && matchOk;

    async function handleSubmit(e) {
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitErr("");
        setSubmitting(true);
        try {
            await setProposalPassword(pw1);
            track("password_set_ok", {
                context: "quote_modal", proposal_id: proposalId,
            });
            onDone?.({ status: "ok" });
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (status === 400 && detail === "password_breached") {
                setValidation((v) => ({ ...v, breached: true }));
                setSubmitErr("breached");
            } else if (status === 401) {
                setSubmitErr("sessionExpired");
            } else {
                setSubmitErr("network");
            }
            track("password_set_error", {
                context: "quote_modal", status, detail,
            });
            // Background fallback — ensure the user can still recover even
            // if the inline submit fails. setup-email is enumeration-safe
            // and no-ops if the row already has a password, so it's safe
            // to fire unconditionally.
            if (email) {
                try {
                    await requestEmployerSetupEmail(email);
                    track("password_setup_email_fallback", {
                        proposal_id: proposalId,
                    });
                } catch (_) { /* never let the fallback surface */ }
            }
        } finally {
            setSubmitting(false);
        }
    }

    async function handleSkip() {
        if (skipping || skipSent) return;
        setSkipping(true);
        try {
            if (email) {
                await requestEmployerSetupEmail(email);
            }
            track("password_modal_skip", { proposal_id: proposalId });
            setSkipSent(true);
            // Give the success state a beat to read, then dismiss.
            setTimeout(() => onDone?.({ status: "skipped" }), 1500);
        } catch (_) {
            // Worst case — just dismiss. The user can still use
            // "Forgot password" from the sign-in page later.
            onDone?.({ status: "skipped" });
        } finally {
            setSkipping(false);
        }
    }

    const feedbackKey = (() => {
        if (validation.breached) return "auth.setPassword.feedback.breached";
        if (pw1.length > 0 && pw1.length < SP_MIN_LEN) {
            return "auth.setPassword.feedback.tooShort";
        }
        if (validation.length_ok && !validation.checking) {
            return "auth.setPassword.feedback.longEnough";
        }
        return null;
    })();
    const feedbackTone = validation.breached
        ? "warn" : validation.length_ok ? "ok" : "muted";
    const matchTone = pw2.length === 0 ? "muted" : matchOk ? "ok" : "warn";
    const submitErrKey = {
        breached: "auth.setPassword.feedback.breached",
        sessionExpired: "auth.setPassword.errors.sessionExpired",
        network: "auth.setPassword.errors.network",
    }[submitErr];

    return (
        <div
            className="reveal-overlay active"
            data-testid="post-quote-password-modal-overlay"
            onClick={(e) => { e.stopPropagation(); }}
        >
            <div
                className="reveal-modal post-quote-password-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="pq-password-modal-title"
                data-testid="post-quote-set-password"
            >
                <div className="set-password-inner">
                    <p className="set-password-eyebrow mono">
                        {t("auth.setPassword.quoteEyebrow")}
                    </p>
                    <h3 className="set-password-title" id="pq-password-modal-title">
                        {firstName
                            ? t("auth.setPassword.quoteHeadingNamed", { name: firstName, defaultValue: `${firstName}, secure your quote` })
                            : t("auth.setPassword.quoteHeading")}
                    </h3>
                    <p className="set-password-sub">
                        {t("auth.setPassword.subhead")}
                    </p>

                    {submitErrKey && (
                        <div
                            className="set-password-error-banner"
                            role="status"
                            data-testid="post-quote-set-password-error"
                        >
                            {t(submitErrKey)}
                        </div>
                    )}

                    {skipSent && (
                        <div
                            className="set-password-error-banner"
                            role="status"
                            data-testid="post-quote-set-password-skip-confirm"
                            style={{ background: "#f1ede6", color: "#231f1a", borderColor: "#d9d4c9" }}
                        >
                            {t("auth.setPassword.skipConfirm", {
                                defaultValue: "We just emailed you a setup link. Check your inbox.",
                            })}
                        </div>
                    )}

                    <form
                        className="set-password-form"
                        onSubmit={handleSubmit}
                        autoComplete="on"
                        data-testid="post-quote-set-password-form"
                        noValidate
                    >
                        <label className="set-password-label mono" htmlFor="pq-set-pw">
                            {t("auth.setPassword.newPasswordLabel")}
                        </label>
                        <input
                            id="pq-set-pw"
                            ref={firstInputRef}
                            type="password"
                            name="new-password"
                            autoComplete="new-password"
                            minLength={SP_MIN_LEN}
                            required
                            value={pw1}
                            onChange={(e) => setPw1(e.target.value)}
                            className="set-password-input"
                            data-testid="post-quote-set-password-new"
                        />
                        <div
                            className={`set-password-feedback set-password-feedback-${feedbackTone}`}
                            aria-live="polite"
                            data-testid="post-quote-set-password-feedback"
                        >
                            {feedbackKey ? t(feedbackKey, { min: SP_MIN_LEN }) : "\u00A0"}
                        </div>

                        <label className="set-password-label mono" htmlFor="pq-set-pw-confirm">
                            {t("auth.setPassword.confirmPasswordLabel")}
                        </label>
                        <input
                            id="pq-set-pw-confirm"
                            type="password"
                            name="confirm-password"
                            autoComplete="new-password"
                            required
                            value={pw2}
                            onChange={(e) => setPw2(e.target.value)}
                            className="set-password-input"
                            data-testid="post-quote-set-password-confirm"
                        />
                        <div
                            className={`set-password-feedback set-password-feedback-${matchTone}`}
                            aria-live="polite"
                        >
                            {pw2.length > 0
                                ? matchOk
                                    ? t("auth.setPassword.feedback.matched")
                                    : t("auth.setPassword.feedback.mismatched")
                                : "\u00A0"}
                        </div>

                        <button
                            type="submit"
                            className="set-password-submit"
                            disabled={!canSubmit}
                            data-testid="post-quote-set-password-submit"
                        >
                            {submitting
                                ? t("auth.setPassword.submitting")
                                : t("auth.setPassword.submit")}
                        </button>

                        <button
                            type="button"
                            onClick={handleSkip}
                            disabled={skipping || skipSent}
                            className="set-password-skip"
                            data-testid="post-quote-set-password-skip"
                            style={{
                                marginTop: "0.75rem",
                                background: "none",
                                border: "none",
                                textDecoration: "underline",
                                cursor: "pointer",
                                color: "#231f1a",
                                opacity: 0.7,
                                fontSize: "0.85rem",
                            }}
                        >
                            {skipping
                                ? t("auth.setPassword.skipSending", { defaultValue: "Sending setup link…" })
                                : t("auth.setPassword.skip", { defaultValue: "Email me a setup link instead" })}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------- */
/* PostQuote — peer-share panel (kept as-is, shown under the result)    */
/* -------------------------------------------------------------------- */
function PostQuote({ firstName, companyName, submitterEmail, proposalId }) {
    const { t } = useTranslation();
    const [recipients, setRecipients] = useState([{ name: "", company: "", email: "" }]);
    const [note, setNote] = useState("");
    const [sent, setSent] = useState(null);
    const [err, setErr] = useState("");
    const [badIdx, setBadIdx] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    const addRecipient = () => {
        if (recipients.length >= SHARE_MAX_PEER_RECIPIENTS) return;
        setRecipients((r) => [...r, { name: "", company: "", email: "" }]);
    };
    const updateRecipient = (i, k, val) =>
        setRecipients((r) => r.map((x, idx) => (idx === i ? { ...x, [k]: val } : x)));
    const removeRecipient = (i) =>
        setRecipients((r) => r.filter((_, idx) => idx !== i));

    const onSubmit = async (e) => {
        e.preventDefault();
        setErr("");
        setBadIdx([]);
        const valid = recipients.filter(
            (r) =>
                r.email.trim() &&
                isValidEmail(r.email.trim()),
        );
        if (valid.length === 0) {
            setErr(t("employers.peerShare.needRecipient"));
            return;
        }
        setSubmitting(true);
        track("share_peer_submit", { recipient_count: valid.length });
        try {
            const res = await sharePeer({
                senderName: firstName,
                senderCompany: companyName,
                senderEmail: submitterEmail,
                proposalId,
                note: note.trim(),
                recipients: valid.map((r) => ({
                    name: r.name.trim(),
                    company: r.company.trim(),
                    email: r.email.trim(),
                })),
            });
            setSent({ emailsSent: res.emailsSent, rateLimited: res.rateLimited });
            track("share_peer_submit.success", {
                emails_sent: res.emailsSent,
                rate_limited: !!res.rateLimited,
            });
        } catch (error) {
            track("share_peer_submit.failure", {});
            const idx = badRecipientIndices(error);
            if (idx.length) setBadIdx(idx);
            const msg = formatApiError(error) || t("errors.generic");
            setErr(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="post-quote" data-testid="post-quote">
            <div className="post-quote-main">
                <div className="section-label mono">
                    {t("employers.peerShare.label")}
                </div>
                <h3>
                    <I18nText i18nKey="employers.peerShare.h4" />
                </h3>
                <p className="subtitle">{t("employers.peerShare.body")}</p>
                {sent ? (
                    <div className="share-sent" data-testid="peer-share-sent">
                        <p>
                            <strong>{t("employers.peerShare.sent.status")}</strong>
                        </p>
                        <p>
                            <I18nText
                                i18nKey="employers.peerShare.sent.headline"
                                values={{ count: sent.emailsSent }}
                            />
                        </p>
                        <p>{t("employers.peerShare.sent.body")}</p>
                        <p className="share-compliance">
                            {t("employers.peerShare.compliance")}
                        </p>
                    </div>
                ) : (
                    <form onSubmit={onSubmit}>
                        {recipients.map((r, i) => (
                            <div
                                className={`recipient-row ${badIdx.includes(i) ? "is-bad" : ""}`}
                                key={i}
                            >
                                <input
                                    type="text"
                                    placeholder={t("employers.peerShare.placeholderTheirName")}
                                    value={r.name}
                                    onChange={(e) => updateRecipient(i, "name", e.target.value)}
                                    onBlur={(e) => updateRecipient(i, "name", titleCaseName(e.target.value))}
                                    data-testid={`peer-recipient-name-${i}`}
                                />
                                <input
                                    type="text"
                                    placeholder={t("employers.peerShare.placeholderTheirCompany")}
                                    value={r.company}
                                    onChange={(e) => updateRecipient(i, "company", e.target.value)}
                                    data-testid={`peer-recipient-company-${i}`}
                                />
                                <input
                                    type="email"
                                    placeholder={t("employers.peerShare.placeholderTheirEmail")}
                                    value={r.email}
                                    onChange={(e) => updateRecipient(i, "email", e.target.value)}
                                    onBlur={(e) => updateRecipient(i, "email", e.target.value.trim())}
                                    data-testid={`peer-recipient-email-${i}`}
                                />
                                {recipients.length > 1 && (
                                    <button
                                        type="button"
                                        className="btn-remove"
                                        onClick={() => removeRecipient(i)}
                                        data-testid={`peer-remove-${i}`}
                                        aria-label="Remove recipient"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                        ))}
                        {recipients.length < SHARE_MAX_PEER_RECIPIENTS && (
                            <button
                                type="button"
                                className="btn-add-more"
                                onClick={addRecipient}
                                data-testid="peer-add-more"
                            >
                                {t("employers.peerShare.addMore")}
                            </button>
                        )}
                        <div className="form-field" style={{ marginTop: "1rem" }}>
                            <label className="mono">{t("employers.peerShare.labelNote")}</label>
                            <textarea
                                rows="3"
                                placeholder={t("employers.peerShare.placeholderNote")}
                                maxLength={300}
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                data-testid="peer-note"
                            />
                        </div>
                        {err && (
                            <div
                                className="field-error"
                                data-testid="peer-share-error"
                                style={{ marginBottom: "1rem" }}
                            >
                                {err}
                            </div>
                        )}
                        <button
                            type="submit"
                            className="btn-share"
                            disabled={submitting}
                            data-testid="peer-share-submit"
                        >
                            {submitting
                                ? t("employers.peerShare.submitting")
                                : t("employers.peerShare.submit")}
                        </button>
                        <div className="share-compliance">
                            {t("employers.peerShare.compliance")}
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------- */
/* Page shell — orchestrates form →︎ computing →︎ result                   */
/* -------------------------------------------------------------------- */
export default function Employers() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    // Three-phase state machine: 'form' →︎ 'computing' →︎ 'result'.
    const [phase, setPhase] = useState("form");
    const [accepted, setAccepted] = useState(null);
    const [reinforcementOpen, setReinforcementOpen] = useState(false);
    // Phase 3e — blocking password modal appears the moment the quote
    // result renders. `passwordModalOpen` stays true until the user
    // either sets a password or clicks "email me a setup link" —
    // ensuring every prospect ends up with a recoverable account.
    const [passwordModalOpen, setPasswordModalOpen] = useState(false);
    // Phase 3e polish — once the modal is satisfied (password set OR setup
    // link sent), render a subtle "quote is saved" status strip that deep-
    // links into the employer quotes dashboard. Turns "one more step"
    // fatigue into reassurance.
    const [passwordStatus, setPasswordStatus] = useState(null); // "ok" | "skipped" | null

    useEffect(() => {
        track("employer_view", {});
        document.title = t("employers.title");
    }, [t]);

    const cards = t("employers.cards", { returnObjects: true }) || [];
    const vs = t("employers.vs", { returnObjects: true }) || {};

    const onAccepted = (payload) => {
        setAccepted(payload);
        setPhase("computing");
    };
    const onComputingDone = () => {
        setPhase("result");
        // Phase 3e mobile polish — let the user glimpse the quote result
        // before the blocking password modal covers it. 1800ms is long
        // enough to read the headline "Your numbers: $X–$Y per period"
        // + anchor-scroll to the result, but short enough that it still
        // feels like a forcing function, not a distraction.
        setTimeout(() => setPasswordModalOpen(true), 1800);
    };

    const goToProposal = () => {
        if (accepted?.redirectUrl) {
            track("quote_result_cta_proposal", {
                proposalId: accepted.proposalId,
            });
            navigate(accepted.redirectUrl);
        }
    };

    const showReinforcement = () => {
        track("quote_result_cta_reinforce", {
            proposalId: accepted?.proposalId,
        });
        setReinforcementOpen(true);
    };

    return (
        <section className="lg-section track" data-testid="employer-track">
            <button
                className="back-link"
                onClick={(e) => {
                    e.preventDefault();
                    navigate("/");
                }}
                data-testid="employer-back"
            >
                {t("employers.back")}
            </button>

            <div className="emp-hero">
                <div className="section-label mono">{t("employers.sectionLabel")}</div>
                <h2>
                    <I18nText
                        i18nKey="employers.h2"
                        components={{ span: <span className="big-num" /> }}
                    />
                </h2>
                <p className="emp-hero-sub">{t("employers.heroSub")}</p>
            </div>

            <div className="case-grid" data-testid="case-grid">
                {cards.map((c, i) => (
                    <div className="case-card" key={i}>
                        <div>
                            <div className="case-num">
                                {c.num === "10min" ? (
                                    <>
                                        10<span style={{ fontSize: "1.5rem" }}>min</span>
                                    </>
                                ) : (
                                    c.num
                                )}
                            </div>
                            <h3>{c.title}</h3>
                        </div>
                        <p>{c.body}</p>
                    </div>
                ))}
            </div>

            {/* Mid-page value panel — after the case cards, before the comparison block. */}
            <ValuePanel page="employers" />

            <div className="vs-section">
                <div className="section-label mono">{vs.label}</div>
                <h3>
                    <I18nText i18nKey="employers.vs.headline" />
                </h3>
                <p>{vs.sub}</p>
                <div className="vs-grid-3">
                    <div className="vs-col-3 vs-problem">
                        <div className="vs-label mono">{vs.oldLabel}</div>
                        <h4>{vs.oldTitle}</h4>
                        <ul>
                            {(vs.oldList || []).map((x, i) => <li key={i}>{x}</li>)}
                        </ul>
                    </div>
                    <div className="vs-col-3 vs-outcome">
                        <div className="vs-label mono">{vs.outcomeLabel}</div>
                        <h4>{vs.outcomeTitle}</h4>
                        <ul>
                            {(vs.outcomeList || []).map((x, i) => <li key={i}>{x}</li>)}
                        </ul>
                    </div>
                    <div className="vs-col-3 vs-us">
                        <div className="vs-label mono">{vs.usLabel}</div>
                        <h4>{vs.usTitle}</h4>
                        <ul>
                            {(vs.usList || []).map((x, i) => <li key={i}>{x}</li>)}
                        </ul>
                    </div>
                </div>
            </div>

            {phase === "form" && <QuoteForm onAccepted={onAccepted} />}
            {phase === "computing" && <ComputingState onDone={onComputingDone} />}
            {phase === "result" && accepted && (
                <>
                    <ResultScreen
                        accepted={accepted}
                        onViewProposal={goToProposal}
                        onShowReinforcement={showReinforcement}
                    />
                    {passwordStatus && (
                        <div
                            className="quote-saved-strip"
                            data-testid="quote-saved-strip"
                            role="status"
                        >
                            <span className="quote-saved-strip-dot" aria-hidden="true" />
                            <span className="quote-saved-strip-text">
                                {passwordStatus === "ok"
                                    ? t("employers.savedStrip.saved", { defaultValue: "Your quote is saved · protected" })
                                    : t("employers.savedStrip.linkSent", { defaultValue: "Your quote is saved · setup link in your inbox" })}
                            </span>
                            <button
                                type="button"
                                onClick={() => {
                                    track("quote_saved_strip_cta", { status: passwordStatus });
                                    navigate("/portal/employer/quotes");
                                }}
                                className="quote-saved-strip-cta"
                                data-testid="quote-saved-strip-cta"
                            >
                                {t("employers.savedStrip.cta", { defaultValue: "View all my quotes →" })}
                            </button>
                        </div>
                    )}
                    <ValuePanel page="employers_result" />
                    <PostQuote
                        firstName={accepted.firstName}
                        companyName={accepted.companyName}
                        submitterEmail={accepted.submitterEmail}
                        proposalId={accepted.proposalId}
                    />
                    <PostQuoteSetPasswordModal
                        open={passwordModalOpen}
                        proposalId={accepted.proposalId}
                        firstName={accepted.firstName}
                        email={accepted.submitterEmail}
                        onDone={(result) => {
                            setPasswordModalOpen(false);
                            // Only show the confirmation strip if the modal
                            // actually finished the flow — either directly
                            // (status "ok") or via the setup-email fallback
                            // (status "skipped" triggers /setup-email).
                            if (result?.status) {
                                setPasswordStatus(result.status);
                                track("password_status_strip_shown", {
                                    status: result.status,
                                    proposal_id: accepted.proposalId,
                                });
                            }
                        }}
                    />
                    <ReinforcementModal
                        open={reinforcementOpen}
                        onClose={() => setReinforcementOpen(false)}
                        onPrimaryCta={() => {
                            setReinforcementOpen(false);
                            goToProposal();
                        }}
                        sampleBiweekly={27.26}
                        coverageAmount={50000}
                    />
                </>
            )}
        </section>
    );
}
